import React, { KeyboardEvent, useEffect, useRef, useState } from "react";
import Markdown from "react-markdown";

import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputBase,
  MenuItem,
  Select,
  Typography,
  keyframes,
  styled,
} from "@mui/material";
import {
  BarChart as VisualizeIcon,
  Check as CheckIcon,
  ContentCopyOutlined as CopyIcon,
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  Send as SendIcon,
  TableChart as ExcelIcon,
  TableRows as CsvIcon,
} from "@mui/icons-material";
import { copyToClipboard } from "react-lib/apps/HISV3/common/CommonInterface";

import SnackMessage from "../common/SnackMessage";

import ChartPanel from "./ChartPanel";
import {
  ACTIONS,
  ChartType,
  ChatMessage,
  ChatMessageType,
  ExportFormat,
  LLM_MODEL_OPTIONS,
  LlmModel,
  MAIN_CARD_NAME,
  MeReportSequenceType,
  RunSequence,
  SetProp,
} from "./sequence/MeReport";

type CardReportAgentProps = {
  runSequence: RunSequence;
  setProp: SetProp;
  MeReportSequence?: MeReportSequenceType;
  errorMessage?: any;
  successMessage?: any;
};

const ALIGN_BY_TYPE: Record<ChatMessageType, "start" | "end" | "center"> = {
  system: "center",
  user: "end",
  agent: "start",
  error: "start",
};

const EXPORT_BUTTONS: { format: ExportFormat; label: string; Icon: typeof CsvIcon }[] = [
  { format: "csv", label: "CSV", Icon: CsvIcon },
  { format: "excel", label: "Excel", Icon: ExcelIcon },
];

const RootContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "calc(100vh - 12.5rem)",
  minHeight: "32rem",
  width: "100%",
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: "0.75rem",
  overflow: "hidden",
});

const Header = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  padding: "12px 20px",
  borderBottom: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
});

const HeaderTitle = styled(Typography)({
  fontSize: "1rem",
  fontWeight: 600,
  color: "#1e293b",
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const TitleAccent = styled(Box)({
  width: "4px",
  height: "20px",
  borderRadius: "2px",
  backgroundColor: "#e91e63",
});

const HeaderControls = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});

const ModelSelect = styled(Select)({
  fontSize: "0.8rem",
  minWidth: "10rem",
  backgroundColor: "#f8fafc",
  borderRadius: "0.5rem",
  "& .MuiOutlinedInput-notchedOutline": { border: "1px solid #e2e8f0" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#e91e63" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#e91e63" },
  "& .MuiSelect-select": { padding: "8px 12px" },
});

const ResetButton = styled(Button)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  padding: "6px 12px",
  color: "#64748b",
  fontSize: "0.8rem",
  textTransform: "none",
  borderRadius: "0.5rem",
  "&:hover": { backgroundColor: "#f1f5f9", color: "#e91e63" },
});

const HistoryPanel = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: "20px 24px",
  backgroundColor: "#f8fafc",
  display: "flex",
  flexDirection: "column",
  gap: "12px",
});

const MessageRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "align",
})<{ align: "start" | "end" | "center" }>(({ align }) => ({
  display: "flex",
  justifyContent: align === "start" ? "flex-start" : align === "end" ? "flex-end" : "center",
}));

const SystemBubble = styled(Box)({
  maxWidth: "85%",
  padding: "8px 14px",
  borderRadius: "9999px",
  backgroundColor: "#e2e8f0",
  color: "#64748b",
  fontSize: "0.8rem",
  textAlign: "center",
});

const UserBubble = styled(Box)({
  maxWidth: "75%",
  padding: "10px 14px",
  borderRadius: "1rem 1rem 0.25rem 1rem",
  backgroundColor: "#e91e63",
  color: "#ffffff",
  fontSize: "0.875rem",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
});

const AgentBubble = styled(Box)({
  maxWidth: "85%",
  padding: "12px 16px",
  borderRadius: "1rem 1rem 1rem 0.25rem",
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  color: "#1e293b",
  fontSize: "0.875rem",
  wordBreak: "break-word",
  display: "flex",
  flexDirection: "column",
  gap: "10px",
});

const MarkdownText = styled(Box)({
  fontSize: "0.875rem",
  lineHeight: 1.55,
  "& p": { margin: "0 0 0.5em" },
  "& p:last-child": { marginBottom: 0 },
  "& code": {
    fontFamily: "'Fira Code', 'Courier New', monospace",
    fontSize: "0.8125rem",
    padding: "0.1em 0.35em",
    borderRadius: "0.25rem",
    backgroundColor: "#f1f5f9",
    color: "#be185d",
  },
  "& pre": {
    margin: "0.5em 0",
    padding: "10px 12px",
    borderRadius: "0.5rem",
    backgroundColor: "#0f172a",
    color: "#e2e8f0",
    overflowX: "auto",
  },
  "& pre code": { background: "transparent", color: "inherit", padding: 0 },
  "& ul, & ol": { paddingLeft: "1.25em", margin: "0.25em 0" },
  "& a": { color: "#e91e63" },
});

const ErrorBubble = styled(AgentBubble)({
  borderColor: "#fecaca",
  backgroundColor: "#fef2f2",
  color: "#b91c1c",
});

const SqlBlockWrapper = styled(Box)({
  position: "relative",
});

const SqlBlock = styled(Box)({
  fontFamily: "'Fira Code', 'Courier New', monospace",
  fontSize: "0.75rem",
  backgroundColor: "#0f172a",
  color: "#e2e8f0",
  padding: "10px 36px 10px 12px",
  borderRadius: "0.5rem",
  overflowX: "auto",
  whiteSpace: "pre",
});

const CopyIconButton = styled(IconButton)({
  position: "absolute",
  top: "4px",
  right: "4px",
  width: "26px",
  height: "26px",
  color: "#94a3b8",
  backgroundColor: "transparent",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.08)", color: "#ffffff" },
});

const CopyTextButton = styled(IconButton)({
  width: "24px",
  height: "24px",
  color: "#94a3b8",
  alignSelf: "flex-end",
  "&:hover": { color: "#e91e63", backgroundColor: "#fff0f3" },
});

const RetryButton = styled(Button)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  padding: "4px 10px",
  fontSize: "0.75rem",
  fontWeight: 500,
  textTransform: "none",
  borderRadius: "0.5rem",
  border: "1px solid #fecaca",
  color: "#b91c1c",
  backgroundColor: "#ffffff",
  alignSelf: "flex-start",
  marginTop: "6px",
  "&:hover": { backgroundColor: "#fef2f2", borderColor: "#b91c1c" },
});

const SectionLabel = styled(Typography)({
  fontSize: "0.7rem",
  fontWeight: 600,
  color: "#64748b",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  marginBottom: "4px",
});

const ExportRow = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: "8px",
  paddingTop: "4px",
  borderTop: "1px solid #f1f5f9",
});

const ExportButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>(({ active }) => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  minWidth: 0,
  padding: "2px 8px",
  fontSize: "0.7rem",
  fontWeight: 500,
  lineHeight: 1.4,
  textTransform: "none",
  borderRadius: "0.375rem",
  border: `1px solid ${active ? "#e91e63" : "#e2e8f0"}`,
  color: active ? "#e91e63" : "#475569",
  backgroundColor: active ? "#fff0f3" : "#ffffff",
  "& .MuiButton-startIcon": { marginRight: 2 },
  "&:hover": { borderColor: "#e91e63", color: "#e91e63", backgroundColor: "#fff" },
  "&:disabled": { color: "#cbd5e1" },
}));

const dotBounce = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40%           { transform: scale(1);   opacity: 1;   }
`;

const LoadingDots = styled(Box)({
  display: "flex",
  gap: "4px",
  padding: "12px 16px",
  borderRadius: "1rem 1rem 1rem 0.25rem",
  backgroundColor: "#ffffff",
  border: "1px solid #e2e8f0",
  width: "fit-content",
  "& .dot": {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: "#e91e63",
    animation: `${dotBounce} 1.2s infinite ease-in-out`,
  },
  "& .dot:nth-of-type(2)": { animationDelay: "0.15s" },
  "& .dot:nth-of-type(3)": { animationDelay: "0.3s" },
});

const InputWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  padding: "12px 16px",
  borderTop: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
});

const QueryInput = styled(InputBase)({
  flex: 1,
  fontSize: "0.875rem",
  color: "#1e293b",
  padding: "8px 14px",
  backgroundColor: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "9999px",
  "&:focus-within": { borderColor: "#e91e63", backgroundColor: "#ffffff" },
  "& input::placeholder": { color: "#94a3b8" },
});

const SendButton = styled(IconButton)({
  backgroundColor: "#e91e63",
  color: "#ffffff",
  width: "36px",
  height: "36px",
  "&:hover": { backgroundColor: "#c2185b" },
  "&:disabled": { backgroundColor: "#f8bbd9", color: "#ffffff" },
});

const COPIED_RESET_MS = 2000;

const CardReportAgent = (props: CardReportAgentProps) => {
  const seq = props.MeReportSequence;
  const historyRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [draft, setDraft] = useState("");
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  // -1 = not browsing history; otherwise 0-based index into userQueries (0 = most recent)
  const [historyIndex, setHistoryIndex] = useState(-1);

  const messages = seq?.messages || [];
  const llmModel = seq?.llmModel || "gpt-4o";
  const isLoading = !!seq?.isLoading;
  const canSend = draft.trim().length > 0 && !isLoading;

  // Reversed list of past user queries (index 0 = most recent) for ↑/↓ recall
  const userQueries = messages
    .filter((m: ChatMessage) => m.type === "user")
    .map((m: ChatMessage) => m.text)
    .reverse();

  useEffect(() => {
    if (!seq?.sequenceIndex) {
      props.runSequence({ sequence: "MeReport" });
    }
    inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll the chat container, not the page, to avoid the whole window bouncing
  useEffect(() => {
    const el = historyRef.current;

    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, isLoading]);

  const handleSend = () => {
    if (!canSend) return;

    props.runSequence({
      sequence: "MeReport",
      action: ACTIONS.SEND_QUERY,
      query: draft.trim(),
    });

    setDraft("");
    setHistoryIndex(-1);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }

    // ↑/↓ recall past user queries. ↑ only triggers when input is empty so it
    // doesn't hijack cursor navigation inside a multi-line draft.
    if (e.key === "ArrowUp" && (historyIndex >= 0 || draft.length === 0)) {
      if (userQueries.length === 0) return;

      const next = Math.min(historyIndex + 1, userQueries.length - 1);

      if (next === historyIndex) return;

      e.preventDefault();
      setHistoryIndex(next);
      setDraft(userQueries[next]);

      return;
    }

    if (e.key === "ArrowDown" && historyIndex >= 0) {
      e.preventDefault();

      const next = historyIndex - 1;

      setHistoryIndex(next);
      setDraft(next < 0 ? "" : userQueries[next]);

      return;
    }

    if (!(e.key === "Escape" && historyIndex >= 0)) {
      return;
    }

    e.preventDefault();

    setHistoryIndex(-1);
    setDraft("");
  };

  const handleReset = () => {
    if (window.confirm("Clear conversation history and start fresh?")) {
      props.runSequence({ sequence: "MeReport", action: ACTIONS.RESET_SESSION });
    }
  };

  const handleChangeModel = (value: LlmModel) => {
    props.runSequence({ sequence: "MeReport", action: ACTIONS.SET_LLM, llmModel: value });
  };

  const handleExport = (messageId: string, format: ExportFormat) => {
    props.runSequence({ sequence: "MeReport", action: ACTIONS.EXPORT_FILE, messageId, format });
  };

  const handleCopy = async (key: string, text: string) => {
    await copyToClipboard(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), COPIED_RESET_MS);
  };

  const handleRetry = (query: string) => {
    props.runSequence({ sequence: "MeReport", action: ACTIONS.SEND_QUERY, query });
  };

  const handleToggleChart = (messageId: string) => {
    props.runSequence({ sequence: "MeReport", action: ACTIONS.TOGGLE_CHART, messageId });
  };

  const handleChangeChartType = (messageId: string, chartType: ChartType) => {
    props.runSequence({
      sequence: "MeReport",
      action: ACTIONS.SET_CHART_TYPE,
      messageId,
      chartType,
    });
  };

  const renderMessage = (msg: ChatMessage) => {
    switch (msg.type) {
      case "system":
        return <SystemBubble>{msg.text}</SystemBubble>;

      case "user":
        return <UserBubble>{msg.text}</UserBubble>;

      case "error":
        return (
          <ErrorBubble>
            {msg.text}
            {msg.retryQuery && (
              <RetryButton
                onClick={() => handleRetry(msg.retryQuery!)}
                disabled={isLoading}
                startIcon={<ReplayIcon sx={{ fontSize: 14 }} />}
              >
                Retry
              </RetryButton>
            )}
          </ErrorBubble>
        );

      case "agent": {
        const textCopyKey = `text-${msg.id}`;
        const sqlCopyKey = `sql-${msg.id}`;
        return (
          <AgentBubble>
            {msg.text && (
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <MarkdownText>
                  <Markdown>{msg.text}</Markdown>
                </MarkdownText>
                <CopyTextButton onClick={() => handleCopy(textCopyKey, msg.text)} title="Copy text">
                  {copiedKey === textCopyKey ? (
                    <CheckIcon sx={{ fontSize: 14, color: "#16a34a" }} />
                  ) : (
                    <CopyIcon sx={{ fontSize: 14 }} />
                  )}
                </CopyTextButton>
              </Box>
            )}

            {msg.rawSql && (
              <Box>
                <SectionLabel>SQL Query</SectionLabel>
                <SqlBlockWrapper>
                  <SqlBlock>{msg.rawSql}</SqlBlock>
                  <CopyIconButton
                    onClick={() => handleCopy(sqlCopyKey, msg.rawSql!)}
                    title="Copy SQL"
                  >
                    {copiedKey === sqlCopyKey ? (
                      <CheckIcon sx={{ fontSize: 14, color: "#4ade80" }} />
                    ) : (
                      <CopyIcon sx={{ fontSize: 14 }} />
                    )}
                  </CopyIconButton>
                </SqlBlockWrapper>
              </Box>
            )}

            {msg.rawSql && (
              <ExportRow>
                <SectionLabel sx={{ marginRight: "auto", marginBottom: 0 }}>
                  Export Results
                </SectionLabel>
                {EXPORT_BUTTONS.map(({ format, label, Icon }) => {
                  const loading = !!msg.exportLoading?.[format];
                  return (
                    <ExportButton
                      key={format}
                      onClick={() => handleExport(msg.id, format)}
                      disabled={loading}
                      startIcon={
                        loading ? (
                          <CircularProgress size={12} sx={{ color: "inherit" }} />
                        ) : (
                          <Icon sx={{ fontSize: 14 }} />
                        )
                      }
                    >
                      {label}
                    </ExportButton>
                  );
                })}
                <ExportButton
                  active={!!msg.chartVisible}
                  onClick={() => handleToggleChart(msg.id)}
                  disabled={!!msg.chartLoading}
                  startIcon={
                    msg.chartLoading ? (
                      <CircularProgress size={12} sx={{ color: "inherit" }} />
                    ) : (
                      <VisualizeIcon sx={{ fontSize: 14 }} />
                    )
                  }
                >
                  Visualize
                </ExportButton>
              </ExportRow>
            )}

            {msg.chartVisible && msg.chartData && (
              <ChartPanel
                chartData={msg.chartData}
                chartType={msg.chartType || "bar"}
                onChangeChartType={(chartType) => handleChangeChartType(msg.id, chartType)}
              />
            )}
          </AgentBubble>
        );
      }
    }
  };

  return (
    <>
      <SnackMessage
        onEvent={() => {}}
        error={props.errorMessage?.[MAIN_CARD_NAME]}
        success={props.successMessage?.[MAIN_CARD_NAME]}
        onClose={() => props.setProp(`errorMessage.${MAIN_CARD_NAME}`, null)}
      />

      <RootContainer>
        <Header>
          <HeaderTitle>
            <TitleAccent />
            Data Intelligence Agent
          </HeaderTitle>

          <HeaderControls>
            <ModelSelect
              value={llmModel}
              size="small"
              onChange={(e) => handleChangeModel(e.target.value as LlmModel)}
            >
              {LLM_MODEL_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "0.8rem" }}>
                  {opt.label}
                </MenuItem>
              ))}
            </ModelSelect>

            <ResetButton onClick={handleReset} disabled={isLoading}>
              <RefreshIcon sx={{ fontSize: 16 }} />
              Reset
            </ResetButton>
          </HeaderControls>
        </Header>

        <HistoryPanel ref={historyRef}>
          {messages.map((msg) => (
            <MessageRow key={msg.id} align={ALIGN_BY_TYPE[msg.type]}>
              {renderMessage(msg)}
            </MessageRow>
          ))}

          {isLoading && (
            <MessageRow align="start">
              <LoadingDots>
                <div className="dot" />
                <div className="dot" />
                <div className="dot" />
              </LoadingDots>
            </MessageRow>
          )}
        </HistoryPanel>

        <InputWrapper>
          <QueryInput
            inputRef={inputRef}
            placeholder="Query the database, search context, or export reports..."
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
          <SendButton onClick={handleSend} disabled={!canSend}>
            {isLoading ? (
              <CircularProgress size={16} sx={{ color: "inherit" }} />
            ) : (
              <SendIcon sx={{ fontSize: 18 }} />
            )}
          </SendButton>
        </InputWrapper>
      </RootContainer>
    </>
  );
};

CardReportAgent.displayName = "CardReportAgent";

export default React.memo(CardReportAgent);
